import { addHours, isBefore } from "date-fns";

import {
  buildComponentMetadata,
  isComponentCompleted,
  isComponentInProgressOrOverdue,
  isComponentUpcomingOrSoon,
} from "@/components/pages_logged_in/program/components/componentMetadata";
import { Callout } from "@/components/ui/callout";
import {
  LeadComponent,
  MainComponent,
  ParticipantComponent,
  StatisticComponent,
} from "@/types/programs";

type ProgramComponentType = (
  | LeadComponent
  | ParticipantComponent
  | MainComponent
  | StatisticComponent
) & {
  roleplayScenarios?: LeadComponent["roleplayScenarios"];
  totalParticipants?: LeadComponent["totalParticipants"];
};

interface ProgramComponentStatusInfoProps {
  component: ProgramComponentType;
  role?: "lead" | "participant";
}

export function ProgramComponentStatusInfo({
  component,
  role = "participant",
}: ProgramComponentStatusInfoProps) {
  const { status, isSetupComplete, totalParticipants } = component;
  const isProgramLead = role === "lead";
  const metaData = buildComponentMetadata({
    component,
    isProgramLead,
  });

  const isEmptyParticipant = !isSetupComplete || (totalParticipants ?? 0) === 0;

  const isRolePlayNoScenario =
    !component.roleplayScenarios?.length && metaData.isAIRoleplay;

  const next24Hours = addHours(new Date(), 24);

  // only work for upcoming components
  const isAtLeast24HoursBeforeMeeting = component?.targetStartDate
    ? isBefore(new Date(component.targetStartDate), next24Hours)
    : false;

  return (
    <>
      {/* Empty Scenario Warning */}
      {isRolePlayNoScenario &&
        (isComponentInProgressOrOverdue(status) ||
          isComponentUpcomingOrSoon(status)) &&
        isProgramLead && (
          <Callout variant="warning">
            No scenarios have been added to this {metaData.componentTypeName}.
            It will not be visible to participants until scenarios are added.
          </Callout>
        )}

      {/* Empty Participant Warning */}

      {isEmptyParticipant &&
        isComponentInProgressOrOverdue(status) &&
        isProgramLead && (
          <Callout variant="warning">
            No participants have been added to this {metaData.componentTypeName}
            . Please add participants to get started.
          </Callout>
        )}

      {/* invalidDependency Info */}

      {metaData.invalidDependency &&
        ["Ongoing", "Upcoming"].includes(status) &&
        isProgramLead && (
          <Callout variant="warning">{metaData.invalidDependency}</Callout>
        )}

      {/* upcomingInfo for Lead and Participant */}
      {(metaData.isSurvey ||
        metaData.isActionItems ||
        metaData.isAIRoleplay ||
        metaData.isOneOneMaster ||
        metaData.isOneOneMeetingIntro ||
        metaData.isVideo ||
        metaData.isArticle ||
        (metaData.isGroupMeeting && isAtLeast24HoursBeforeMeeting) ||
        (metaData.isAnnouncement && isProgramLead)) &&
        isComponentUpcomingOrSoon(status) &&
        metaData.upcomingInfo && (
          <Callout variant="info" icon={null}>
            {metaData.upcomingInfo}
          </Callout>
        )}

      {/* ongoingInfo for Lead and Participant */}
      {(metaData.isOneOneMaster ||
        metaData.isGroupMeeting ||
        metaData.isVideo ||
        metaData.isArticle ||
        metaData.isAIRoleplay ||
        metaData.isActionItems ||
        metaData.isSurvey) &&
        isComponentInProgressOrOverdue(status) &&
        !!metaData.ongoingInfo && (
          <Callout
            variant={metaData.isGroupMeeting ? "success" : "info"}
            icon={null}
          >
            {metaData.ongoingInfo}
          </Callout>
        )}

      {isComponentCompleted(status) && !!metaData.completedInfo && (
        <Callout variant={"info"} icon={null}>
          {metaData.completedInfo}
        </Callout>
      )}
    </>
  );
}
